import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { Link } from "react-router-dom";

const industries = [
  {
    tag: "01",
    name: "MED SPAS",
    color: "bg-black",
    problem: {
      headline: "Missed Inquiries Kill Revenue",
      body:
        "67% of med spa inquiries go unanswered during business hours. Your front desk can't respond to every DM, web form, and call simultaneously. Every gap is a booked appointment handed to a competitor.",
    },
    system: {
      name: "AI LEAD CAPTURE + BOOKING SYSTEM",
      bullets: [
        "24/7 AI-powered inquiry response across all channels",
        "Instant consultation booking with real-time calendar sync",
        "Automated treatment follow-up and rebooking sequences",
        "Missed inquiry recovery within 5 minutes guaranteed",
      ],
    },
    outcomes: [
      { metric: "+58%", label: "BOOKED CONSULTATIONS" },
      { metric: "3.2x", label: "FASTER RESPONSE TIME" },
      { metric: "+41%", label: "REPEAT TREATMENT BOOKINGS" },
    ],
  },
  {
    tag: "02",
    name: "CLINICAL PRACTICES",
    color: "bg-[#1A1A1A]",
    problem: {
      headline: "Administrative Bottlenecks Drain Resources",
      body:
        "Clinical staff spending 6+ hours daily on intake forms, scheduling calls, and manual follow-ups. This isn't patient care—it's operational overhead that compounds into burnout and capacity limits.",
    },
    system: {
      name: "CLINICAL AUTOMATION INFRASTRUCTURE",
      bullets: [
        "Compliance-ready digital intake and onboarding workflows",
        "Predictive scheduling to minimize gaps and no-shows",
        "Automated referral follow-up and care coordination",
        "Administrative task elimination without sacrificing standards",
      ],
    },
    outcomes: [
      { metric: "-67%", label: "ADMIN TIME REDUCTION" },
      { metric: "94%", label: "SCHEDULING ACCURACY" },
      { metric: "+51%", label: "PATIENT CAPACITY INCREASE" },
    ],
  },
  {
    tag: "03",
    name: "WELLNESS BRANDS",
    color: "bg-black",
    problem: {
      headline: "Single-Purchase Customers Limit Lifetime Value",
      body:
        "Wellness brands invest heavily in acquisition but 73% of customers never return after the first visit. Without structured lifecycle systems, you're constantly paying to replace customers instead of retaining them.",
    },
    system: {
      name: "LIFECYCLE ENGAGEMENT SYSTEM",
      bullets: [
        "Automated journey mapping from first visit to loyal customer",
        "Recurring booking triggers based on service intervals",
        "Re-engagement campaigns for lapsed clients",
        "Retention analytics to identify churn risk early",
      ],
    },
    outcomes: [
      { metric: "+62%", label: "REPEAT BOOKINGS" },
      { metric: "2.8x", label: "CUSTOMER LIFETIME VALUE" },
      { metric: "73%", label: "RETENTION RATE" },
    ],
  },
];

const adaptFeatures = [
  {
    title: "MODULAR ARCHITECTURE",
    desc: "Core infrastructure stays constant. Workflows, triggers, and patient touchpoints configure to your operational model.",
  },
  {
    title: "COMPLIANCE-READY",
    desc: "Built with healthcare compliance in mind. Systems designed around your regulatory and operational constraints.",
  },
  {
    title: "SCALABLE INFRASTRUCTURE",
    desc: "From 10 to 10,000 patients—the system scales without requiring manual rebuilds or additional staff.",
  },
];

const IndustriesPage = () => {
  return (
    <div className="bg-black min-h-screen" style={{ fontFamily: "Arial, sans-serif" }}>
      <Navbar />

      {/* Hero */}
      <section className="relative bg-black min-h-[600px] flex flex-col items-center justify-center overflow-hidden pt-20">
        {/* Axis lines */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-[45%] left-[10%] right-[10%] h-px bg-[#FFD100]/15" />
          <div className="absolute top-0 bottom-0 left-1/2 w-px bg-[#FFD100]/10" />
          <div className="absolute top-[45%] left-1/2 w-3 h-3 border border-[#FFD100]/50 -translate-x-1.5 -translate-y-1.5 rotate-45" />
        </div>

        <div className="relative z-10 text-center px-6 md:px-16 max-w-5xl mx-auto py-24">
          <div className="flex items-center justify-center gap-4 mb-8">
            <span className="block w-12 h-px bg-[#FFD100]" />
            <span className="text-[#FFD100] text-xs tracking-[4px] font-bold uppercase">
              INDUSTRIES
            </span>
            <span className="block w-12 h-px bg-[#FFD100]" />
          </div>
          <h1
            className="text-white font-black uppercase leading-none mb-8"
            style={{
              fontFamily: "Arial, sans-serif",
              fontSize: "clamp(2.5rem, 5.5vw, 5rem)",
              letterSpacing: "-1px",
              lineHeight: "1.05",
            }}
          >
            BUILT FOR MODERN
            <br />
            <span className="text-[#FFD100]">HEALTH BUSINESSES</span>
          </h1>
          <p
            className="text-[#E5E5E5] text-base md:text-lg max-w-2xl mx-auto leading-relaxed"
            style={{ lineHeight: "1.7" }}
          >
            We design intelligent systems tailored to how your patients find, engage, and return.
          </p>
        </div>
      </section>

      {/* Industry Blocks */}
      {industries.map((ind, i) => (
        <section key={i} className={`${ind.color} py-20 md:py-28 px-6 md:px-16 border-t border-white/5`}>
          <div className="max-w-7xl mx-auto">
            {/* Industry Header */}
            <div className="mb-12">
              <span className="text-[#FFD100] text-xs font-black tracking-[4px] block mb-3">
                {ind.tag}
              </span>
              <h2
                className="text-white font-black uppercase text-4xl md:text-6xl leading-none"
                style={{ fontFamily: "Arial, sans-serif", letterSpacing: "-1px" }}
              >
                {ind.name}
              </h2>
              <div className="w-24 h-0.5 bg-[#FFD100] mt-6" />
            </div>

            {/* Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12">
              {/* Problem */}
              <div className="border-l-2 border-[#FFD100]/30 pl-6 flex flex-col gap-4">
                <span className="text-[#E5E5E5] text-xs tracking-[3px] font-bold">CHALLENGE</span>
                <h3 className="text-white font-black text-xl leading-tight" style={{ fontFamily: "Arial, sans-serif" }}>
                  {ind.problem.headline}
                </h3>
                <p className="text-[#E5E5E5] text-sm leading-relaxed" style={{ lineHeight: "1.8" }}>
                  {ind.problem.body}
                </p>
              </div>

              {/* System */}
              <div
                className={`${i % 2 === 0 ? "bg-[#1A1A1A]" : "bg-black"} border-l-2 border-[#FFD100] p-6 flex flex-col gap-4`}
              >
                <span className="text-[#FFD100] text-xs tracking-[3px] font-bold">SYSTEM</span>
                <h3 className="text-white font-black text-base leading-tight" style={{ fontFamily: "Arial, sans-serif" }}>
                  {ind.system.name}
                </h3>
                <ul className="flex flex-col gap-2 mt-2">
                  {ind.system.bullets.map((b, j) => (
                    <li key={j} className="flex items-start gap-2 text-[#E5E5E5] text-sm" style={{ lineHeight: "1.7" }}>
                      <span className="text-[#FFD100] font-bold flex-shrink-0 mt-0.5">—</span>
                      {b}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Outcomes */}
              <div className="flex flex-col gap-6">
                <span className="text-[#E5E5E5] text-xs tracking-[3px] font-bold">OUTCOMES</span>
                {ind.outcomes.map((o, j) => (
                  <div key={j} className={`flex flex-col gap-1 ${j > 0 ? "border-t border-[#FFD100]/15 pt-6" : ""}`}>
                    <div
                      className="text-[#FFD100] font-black leading-none"
                      style={{ fontFamily: "Arial, sans-serif", fontSize: "clamp(2rem, 4vw, 3rem)" }}
                    >
                      {o.metric}
                    </div>
                    <span className="text-white text-xs font-bold tracking-[2px]" style={{ fontFamily: "Arial, sans-serif" }}>
                      {o.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* System Adaptation */}
      <section className="bg-[#1A1A1A] py-24 md:py-32 px-6 md:px-16 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-[#FFD100] text-xs tracking-[4px] font-bold block mb-4">INFRASTRUCTURE</span>
            <h2
              className="text-white font-black uppercase text-3xl md:text-5xl leading-tight"
              style={{ fontFamily: "Arial, sans-serif", letterSpacing: "-0.5px" }}
            >
              ONE INFRASTRUCTURE.
              <br />
              <span className="text-[#FFD100]">INFINITE CONFIGURATIONS.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-start max-w-5xl mx-auto">
            <p
              className="text-[#E5E5E5] text-base leading-relaxed"
              style={{ lineHeight: "1.9" }}
            >
              SOLAINVESTAI systems adapt to your business model without custom development
              from scratch. Core infrastructure remains consistent while workflows, triggers,
              and patient touchpoints configure to your specific operational requirements.
              Whether you run a solo practice or a multi-location brand, the system scales
              with you.
            </p>
            <div className="flex flex-col gap-6">
              {adaptFeatures.map((f, i) => (
                <div key={i} className="border-l-2 border-[#FFD100] pl-5 flex flex-col gap-2">
                  <span className="text-[#FFD100] text-sm font-black tracking-[2px]" style={{ fontFamily: "Arial, sans-serif" }}>
                    {f.title}
                  </span>
                  <p className="text-[#E5E5E5] text-sm leading-relaxed" style={{ lineHeight: "1.7" }}>
                    {f.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-black py-24 md:py-36 px-6 md:px-16 relative overflow-hidden border-t border-white/5">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-px bg-[#FFD100]/10 pointer-events-none" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="flex items-center justify-center gap-6 mb-10">
            <div className="w-20 md:w-32 h-px bg-[#FFD100]/40" />
            <div className="w-2 h-2 rotate-45 border border-[#FFD100]/60 flex-shrink-0" />
            <div className="w-20 md:w-32 h-px bg-[#FFD100]/40" />
          </div>
          <h2
            className="text-white font-black uppercase leading-tight mb-8"
            style={{
              fontFamily: "Arial, sans-serif",
              fontSize: "clamp(1.8rem, 4vw, 3.5rem)",
              letterSpacing: "-0.5px",
              lineHeight: "1.1",
            }}
          >
            YOUR INDUSTRY HAS A SYSTEM
            <br />
            PROBLEM.{" "}
            <span className="text-[#FFD100]">WE FIX IT.</span>
          </h2>
          <p className="text-[#E5E5E5] text-sm max-w-lg mx-auto mb-12 leading-relaxed" style={{ lineHeight: "1.8" }}>
            Book a strategy call and get a custom system blueprint for your specific business model and growth stage.
          </p>
          <Link
            to="/contact"
            className="bg-[#FFD100] text-black text-xs font-black tracking-[3px] px-12 py-5 cursor-pointer whitespace-nowrap hover:bg-white transition-all duration-200 inline-block"
            style={{ fontFamily: "Arial, sans-serif" }}
          >
            SCHEDULE A STRATEGY CALL
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default IndustriesPage;
